import React, { useEffect, useState, memo, useCallback } from 'react';
import { Box, Typography, RadioGroup } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import StyledSubmitButton from '../../../../../components/Shared/StyledSubmitButton';
import {
  getIsAddressVerified,
  getOriginalAddress,
  getVerifiedAddress,
} from '../../../store/shipping/shipping.selectors';
import AddressRadioOption from '../AddressRadioOption';
import OriginalAddress from '../../OriginalAddress';
import { resetVerification } from '../../../store/shipping/shipping.actions';
import { AddressOption } from '../../../store/shipping/shipping.types';
import VerifiedAddress from '../../VerifiedAddress';
import useTrackRecipient from '../../../../../hooks/useTrackRecipient';

export interface IAddressSelectionFormProps {
  onSubmit: (option: AddressOption) => void;
}

const AddressSelectionForm = ({ onSubmit }: IAddressSelectionFormProps): JSX.Element => {
  const dispatch = useDispatch();
  const trackEvent = useTrackRecipient();

  const isAddressVerified = useSelector(getIsAddressVerified);
  const verifiedAddress = useSelector(getVerifiedAddress);
  const originalAddress = useSelector(getOriginalAddress);

  const [addressOption, setAddressOption] = useState<AddressOption>(AddressOption.none);

  const isOriginalSelected = addressOption === AddressOption.original;
  const isVerifiedSelected = addressOption === AddressOption.verified;
  const isButtonDisabled = addressOption === AddressOption.none;

  const handleSelectAddressOption = useCallback(({ target }) => setAddressOption(target.value), []);

  const handleEditOriginalAddress = useCallback(() => {
    dispatch(resetVerification());
    trackEvent('Address verification — edit clicked');
  }, [dispatch, trackEvent]);

  const handleSubmit = useCallback(() => {
    onSubmit(addressOption);
  }, [onSubmit, addressOption]);

  useEffect(() => {
    if (isAddressVerified) {
      setAddressOption(AddressOption.verified);
    }
  }, [isAddressVerified]);

  return (
    <Box>
      <RadioGroup value={addressOption} onChange={handleSelectAddressOption}>
        {isAddressVerified && (
          <Box borderRadius="5px" bgcolor="green.fruitSaladLight" width={1} mt={1} mb={3} p={2}>
            <Typography className="Body-Regular-Left-Static">
              We recommend selecting <strong>verified address</strong> to ensure a timely delivery.
            </Typography>
          </Box>
        )}
        <Box mt={2}>
          <Typography className="H4-Dark-Bold">Original address</Typography>
        </Box>
        <AddressRadioOption
          label={originalAddress ? <OriginalAddress address={originalAddress} /> : <></>}
          value={AddressOption.original}
          isActive={isOriginalSelected}
          onEdit={handleEditOriginalAddress}
        />
        {isAddressVerified && (
          <>
            <Box mt={3}>
              <Typography className="H4-Dark-Bold">Verified address</Typography>
            </Box>
            <AddressRadioOption
              label={verifiedAddress ? <VerifiedAddress address={verifiedAddress} /> : <></>}
              value={AddressOption.verified}
              isActive={isVerifiedSelected}
            />
          </>
        )}
        {!isAddressVerified && (
          <Box borderRadius="5px" bgcolor="link.light" width={1} mt={1} p={2}>
            <Typography className="Body-Regular-Left-White">
              We couldn’t verify the address which may <strong>delay a delivery.</strong>
            </Typography>
            <Typography className="Body-Regular-Left-White">
              Please edit or chose the original address to proceed.
            </Typography>
          </Box>
        )}
      </RadioGroup>
      <Box mt={5} mb={3} width={1}>
        <StyledSubmitButton
          data-testid="AddressSelectionForm.Button"
          width="100%"
          disabled={isButtonDisabled}
          onClick={handleSubmit}
        >
          Continue
        </StyledSubmitButton>
      </Box>
    </Box>
  );
};

export default memo<IAddressSelectionFormProps>(AddressSelectionForm);
